import { analytics } from '../firebase';
import { logEvent, setUserId, setUserProperties } from 'firebase/analytics';

// Helper function to safely log events
export const trackEvent = (eventName, parameters = {}) => {
  if (analytics) {
    try {
      logEvent(analytics, eventName, parameters);
      console.log(`✅ Analytics Event: ${eventName}`, parameters);
    } catch (error) {
      console.error('❌ Analytics error:', error);
    }
  } else {
    console.warn(`⚠️ Analytics not available for event: ${eventName}`, parameters);
  }
};

// Track screen views
export const trackScreenView = (screenName) => {
  trackEvent('screen_view', {
    screen_name: screenName,
    screen_class: screenName,
  });
};

// Set user ID and properties for better tracking
export const setUserData = (userId, properties = {}) => {
  if (analytics) {
    try {
      setUserId(analytics, userId);
      setUserProperties(analytics, {
        ...properties,
        last_activity: new Date().toISOString()
      });
      console.log(`✅ User Analytics ID Set: ${userId.substring(0, 8)}...`);
    } catch (error) {
      console.error('Analytics setUserData error:', error);
    }
  }
};

// Track specific user actions for detailed analysis
export const trackUserActivity = (action, details = {}) => {
  trackEvent('user_activity', {
    action: action,
    timestamp: new Date().toISOString(),
    ...details
  });
};

// Authentication events
export const trackAuth = {
  login: (method) => trackEvent('login', { method }),
  logout: () => trackEvent('logout'),
  signup: (method) => trackEvent('sign_up', { method }),
};

// Email-related events
export const trackEmail = {
  view: (emailCount) => trackEvent('view_emails', { 
    email_count: emailCount,
    timestamp: new Date().toISOString() 
  }),
  open: (emailType, hasAttachments = false) => trackEvent('open_email', {
    email_type: emailType,
    has_attachments: hasAttachments,
  }),
  process: (emailType, processingTime) => trackEvent('process_email', {
    email_type: emailType,
    processing_time_ms: processingTime,
  }),
  delete: () => trackEvent('delete_email'),
};

// TLDR events
export const trackTLDR = {
  generate: (emailLength, tldrLength, processingTime, success = true) => trackEvent('generate_tldr', {
    email_length: emailLength,
    tldr_length: tldrLength,
    processing_time_ms: processingTime,
    success: success,
  }),
  view: () => trackEvent('view_tldr'),
  copy: () => trackEvent('copy_tldr'),
};

// Calendar/Event events
export const trackCalendar = {
  createEvent: (source, eventType, hasReminder = false) => trackEvent('create_event', {
    event_source: source,
    event_type: eventType,
    has_reminder: hasReminder,
  }),
  updateEvent: () => trackEvent('update_event'),
  deleteEvent: () => trackEvent('delete_event'),
  shareEvent: (method, recipientCount) => trackEvent('share_event', {
    share_method: method,
    recipient_count: recipientCount,
  }),
  syncCalendar: (eventsCount, direction) => trackEvent('sync_calendar', {
    events_synced: eventsCount,
    sync_direction: direction,
  }),
  viewCalendar: () => trackEvent('view_calendar'),
};

// Feature usage events
export const trackFeature = {
  search: (searchType, resultsCount) => trackEvent('search', {
    search_type: searchType,
    results_count: resultsCount,
  }),
  filter: (filterType) => trackEvent('apply_filter', {
    filter_type: filterType,
  }),
  settings: (settingChanged) => trackEvent('change_setting', {
    setting: settingChanged,
  }),
};

// Session tracking
export const trackSession = {
  start: () => {
    const sessionStartTime = Date.now();
    sessionStorage.setItem('session_start', sessionStartTime);
    trackEvent('session_start');
    
    // Start engagement tracking
    trackSession.startEngagementTracking();
  },
  end: () => {
    const startTime = sessionStorage.getItem('session_start');
    if (startTime) {
      const duration = Math.floor((Date.now() - parseInt(startTime)) / 1000);
      const durationMs = duration * 1000;
      
      // Track session end
      trackEvent('session_end', {
        duration_seconds: duration,
      });
      
      // Track final engagement with proper Firebase format
      if (analytics && duration > 0) {
        try {
          logEvent(analytics, 'user_engagement', {
            engagement_time_msec: durationMs
          });
          console.log(`✅ Final Engagement: ${duration}s (${durationMs}ms)`);
        } catch (error) {
          console.error('❌ Final engagement tracking error:', error);
        }
      }
      
      sessionStorage.removeItem('session_start');
    }
    
    // Stop engagement tracking
    trackSession.stopEngagementTracking();
  },
  
  // Track user engagement every 10 seconds using Firebase's expected format
  startEngagementTracking: () => {
    // Clear any existing interval
    const existingInterval = sessionStorage.getItem('engagement_interval');
    if (existingInterval) {
      clearInterval(parseInt(existingInterval));
    }
    
    const interval = setInterval(() => {
      const startTime = sessionStorage.getItem('session_start');
      if (startTime && !document.hidden) {
        const currentEngagement = Date.now() - parseInt(startTime);
        
        // Use Firebase's standard engagement event
        if (analytics) {
          try {
            logEvent(analytics, 'user_engagement', {
              engagement_time_msec: currentEngagement
            });
            console.log(`✅ User Engagement: ${Math.floor(currentEngagement / 1000)}s`);
          } catch (error) {
            console.error('❌ Engagement tracking error:', error);
          }
        }
      }
    }, 10000); // Every 10 seconds
    
    sessionStorage.setItem('engagement_interval', interval.toString());
  },
  
  stopEngagementTracking: () => {
    const interval = sessionStorage.getItem('engagement_interval');
    if (interval) {
      clearInterval(parseInt(interval));
      sessionStorage.removeItem('engagement_interval');
    }
  }
};

// Error tracking
export const trackError = (errorType, errorMessage) => {
  trackEvent('app_error', {
    error_type: errorType,
    error_message: errorMessage,
  });
};